import { ts } from '@shared/lib/diagTs';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime } from './RealtimeProvider';
// Dev/demo-only positions diagnostics (single-line copy-pasteable console output). Deliberate
// feature import into core: positionsLog is a dependency-free leaf module (no runtime coupling).
import { plog } from '@features/positions/lib/positionsLog';

/**
 * Wires server-pushed events to React Query cache invalidations. Drop this in once near the app root.
 * Each push is treated as a hint — TanStack Query will refetch only mounted queries that match.
 *
 * Bursts are COALESCED: a live CQG session fans out many `trades.changed` / `correlations.changed`
 * pushes in quick succession (one per fill / rebuild), and each one would otherwise invalidate 2–3
 * query families that each fan out to several mounted dashboard queries. Un-debounced, a burst
 * trips the BE per-user rate limiter (100 req / 10 s) → 429 storm. We accumulate the distinct
 * query keys and flush once the pushes settle (trailing 200 ms), capped at 800 ms so a continuous
 * stream still refreshes regularly.
 *
 * The debounce was 800/2000 ms; it's now 200/800 ms because the BE-side CorrelationRebuildCoalescer
 * already collapses a fill burst into ONE rebuild + ONE `correlations.changed` publish, so the FE no
 * longer needs a long window to absorb a per-fill storm — it can react ~600 ms sooner.
 */
export function useRealtimeInvalidation(): void {
  const realtime = useRealtime();
  const qc = useQueryClient();

  useEffect(() => {
    const pending = new Set<string>();           // JSON-encoded query keys awaiting a flush
    let timer: ReturnType<typeof setTimeout> | null = null;
    let maxTimer: ReturnType<typeof setTimeout> | null = null;

    // Opt-in perf logging (DEV, or localStorage.cqgPerf='1' in prod) to measure CQG fill → FE latency.
    const perf = import.meta.env.DEV || (typeof localStorage !== 'undefined' && localStorage.getItem('cqgPerf') === '1');
    const logRecv = (evt: string, p: { serverTs?: number; flowId?: string }) => {
      if (perf && p.serverTs) console.log('%s [realtime] %s recv flow=%s e2eMs=%d', ts(), evt, p.flowId ?? '?', Date.now() - p.serverTs);
    };

    // Minimum gap between flushes. Each flush fans out to MANY mounted dashboard/positions refetches
    // (the positions layer added several), so during a CQG import — a sustained stream of
    // trades/correlations/positions.changed pushes — an uncapped 200/800 ms cadence blew past the BE's
    // per-user rate limiter → a 429 storm. Capping flushes to ~once/1.5 s keeps the refetch rate well
    // under the limit; isolated events (after an idle gap) still flush in 200 ms, so normal live
    // updates stay responsive.
    const MIN_FLUSH_GAP_MS = 1500;
    let lastFlushAt = -Infinity;                   // Date.now() (not performance.now) so fake timers advance it

    const flush = () => {
      const now = Date.now();
      const since = now - lastFlushAt;
      if (since < MIN_FLUSH_GAP_MS) {
        // Sustained burst — hold off until the min gap elapses (coalesces more; avoids the 429 storm).
        if (timer) clearTimeout(timer);
        timer = setTimeout(flush, MIN_FLUSH_GAP_MS - since);
        return;
      }
      if (timer) { clearTimeout(timer); timer = null; }
      if (maxTimer) { clearTimeout(maxTimer); maxTimer = null; }
      lastFlushAt = now;
      if (perf) console.log('%s [realtime] flush invalidating %d key(s) @t=%s', ts(), pending.size, now);
      for (const k of pending) qc.invalidateQueries({ queryKey: JSON.parse(k) as unknown[] });
      pending.clear();
    };

    const schedule = (...keys: Array<readonly unknown[]>) => {
      for (const key of keys) pending.add(JSON.stringify(key));
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, 200);              // trailing debounce for isolated events
      if (!maxTimer) maxTimer = setTimeout(flush, 800); // steady stream still flushes (then gap-capped)
    };

    const offs: Array<() => void> = [];
    // trades.changed also refreshes ['first-trade'] — a *first* import unlocks the date picker and seeds
    // the default range's start (useSeedDateRangeFromFirstTrade / DateRangeTrigger disabled state).
    // … and ['day-journal'] (Plan My Day, Task 27): the day's AI recap on the Dashboard and the day
    // panel's own facts are derived from the day's fills, so an import must refresh them too.
    offs.push(realtime.subscribe('trades.changed',       (p) => { logRecv('trades.changed', p); schedule(['trades'], ['positions'], ['statistics'], ['correlations'], ['closed-trades'], ['last-update'], ['first-trade'], ['day-journal']); }));
    // correlations.changed also invalidates ['positions'] — the position layer is rebuilt inside the
    // same BE transaction as correlations, so a correlation rebuild implies fresh positions too.
    offs.push(realtime.subscribe('correlations.changed', (p) => { logRecv('correlations.changed', p); schedule(['correlations'], ['statistics'], ['closed-trades'], ['positions'], ['last-update'], ['day-journal']); }));
    // positions.changed → refresh open + closed position queries, stats (position basis) and the
    // freshness stamp. plog = copy-pasteable dev/demo diagnostics (never logs in prod builds).
    offs.push(realtime.subscribe('positions.changed',    (p) => { logRecv('positions.changed', p); plog('positions.changed', p); schedule(['positions'], ['statistics'], ['last-update']); }));
    offs.push(realtime.subscribe('user.profile.updated', () => schedule(['me'])));
    // NOTE: 'subscription.changed' → ['me'] is NOT registered here (deliberately, since 2026-08-13).
    // It lives in `useSubscriptionChangedInvalidation` (`@features/billing`), called unconditionally
    // from `SubscriptionGuard` — this hook only runs inside `AppShell`, which never mounts while
    // `SubscriptionGuard` is blocking an unentitled user behind the paywall, so registering the
    // listener here left it unreachable exactly when it mattered most. See SubscriptionGuard.tsx.
    // §14h — BE fans 'accounts.updated' after upserting a UserAccount (Manual on signup, CQG after
    // AccountsReport) so the AccountDropdown refetches without a manual reload.
    offs.push(realtime.subscribe('accounts.updated',     () => schedule(['trades', 'accounts'])));
    // AI Backtester — a background run finished; refresh the run/strategy/credits queries so the
    // results page swaps its spinner for the results without waiting on the status poll.
    offs.push(realtime.subscribe('aibacktest.run.completed', () => schedule(['ai-backtest'])));
    // A credit-pack purchase was applied by the Stripe webhook — refresh the credits query so the
    // chip, the "Not enough credits" warning and the run gates all pick up the new balance in an
    // already-open tab, without the user reloading. Narrow key on purpose: nothing else about the
    // backtester changed, so re-fetching the whole ['ai-backtest'] family would be noise.
    offs.push(realtime.subscribe('credits.changed', () => schedule(['ai-backtest', 'credits'])));

    return () => {
      for (const off of offs) off();
      if (timer) clearTimeout(timer);
      if (maxTimer) clearTimeout(maxTimer);
    };
  }, [realtime, qc]);
}
